import { useEffect, useState } from "react";

type Service<TData, TParams extends any[]> = (
  ...args: TParams
) => Promise<TData> | TData;

interface Options<TData, TParams extends any[]> {
  onSuccess?: (data: TData, params: TParams) => void;
  onError?: (e: Error, params: TParams) => void;
  manual?: boolean;
  cacheKey?: string;
  refreshDeps?: any[];
}

const DefaultOptions: Options<any, any[]> = {
  manual: false,
};

const cache = new Map<string, Promise<any>>();

export default function useResource<TData, TParams extends any[]>(
  service: Service<TData, TParams>,
  options?: Options<TData, TParams>
) {
  const realOptions = { ...DefaultOptions, ...(options || {}) };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [data, setData] = useState<TData>();

  useEffect(() => {
    if (!realOptions.manual) {
      // @ts-ignore
      fetchFn();
    }
  }, [realOptions.manual, ...(realOptions.refreshDeps || [])]);

  const realService: Service<TData, TParams> = (...args) => {
    let result;
    if (realOptions.cacheKey) {
      const oldPromise = cache.get(realOptions.cacheKey);
      if (oldPromise) {
        result = oldPromise;
      }
    }
    if (!result) {
      result = service(...args);
      !!realOptions.cacheKey && cache.set(realOptions.cacheKey, result);
    }
    return result;
  };

  async function fetchFn(...args: TParams) {
    setLoading(true);
    try {
      const result = await realService(...args);
      setData(result);

      realOptions.onSuccess?.(result, args);
      return result;
    } catch (error) {
      setError(error as any);
      realOptions.onError?.(error as any, args);
    } finally {
      if (realOptions.cacheKey) {
        cache.delete(realOptions.cacheKey);
      }
      setLoading(false);
    }
  }

  return {
    loading,
    error,
    data,
    run: fetchFn,
    refresh: fetchFn,
  };
}
